import { useEffect, useState } from 'react';
import { api, darf, datum, type Anmelder } from '../api.ts';
import {
  Auswahl, Dialog, Fehlerkasten, Haken, Text, ausFehler, type Fehlerlage,
} from './Formular.tsx';
import { Karte, Leer } from './Geruest.tsx';
import { useFrisch } from './Frisch.tsx';
import Abwurf from './Abwurf.tsx';
import { extern } from './extern.ts';

/**
 * Zwei Pflegemasken, die früher eigene Menüpunkte waren.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „und generell will ich, dass du nun alles einmal fertig programmierst,
 *    schaue dir dazu die vorlagen an und baue alle punkte 1:1 NEU in dem
 *    neuen system."
 *
 * ZWEI PFLEGEN IN EINEM BILDSCHIRM: „Ort → Gemeinde" und die Regelpläne.
 */


// ============================================================================
// Ort → Gemeinde
// ============================================================================

/**
 * Welcher Ort zu welcher Verwaltungsgemeinde gehört.
 *
 * WOZU: der Empfänger einer Behördenmail hängt an der GEMEINDE, der Auftrag
 * kennt aber nur einen ORT. „Nauort" ist ein Ort, zuständig ist die
 * Verbandsgemeinde „Ransbach-Baumbach". Ohne die Zuordnung findet der Versand
 * keinen Empfänger, und die Aufgrabungsanzeige geht nirgendwohin.
 *
 * ERST DER BERICHT, DANN DAS SCHREIBEN. Ein Lauf, der 341 Gemeinden ändert und
 * danach „fertig" sagt, ist nicht nachvollziehbar.
 */
export function Ortszuordnung({ ich }: { ich: Anmelder }) {
  const [d, setD] = useState<any>(null);
  const [offen, setOffen] = useState<any[] | null>(null);
  const [neu, setNeu] = useState<any | null>(null);
  const [ergebnis, setErgebnis] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const darfPflegen = darf(ich, 'stammdaten.pflegen');

  async function laden() {
    const [a, b] = await Promise.all([
      api.ortszuordnung(), api.ortszuordnungOffen(),
    ]);
    setD(a); setOffen(b);
  }
  useEffect(() => { laden().catch((f) => setFehler(ausFehler(f))); }, []);
  useFrisch(laden);
  if (!d) return null;

  async function anwenden(korrigieren: boolean) {
    setLaeuft(true); setFehler(null);
    try {
      setErgebnis(await api.ortszuordnungAnwenden(korrigieren));
      await laden();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  async function speichern() {
    setLaeuft(true); setFehler(null);
    try { await api.ortSetzen(neu); setNeu(null); await laden(); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  const z = d.zahlen;

  /* DIE ORTE, DIE WIR HERAUSFILTERN, STEHEN NICHT MEHR MITTEN IN DER LISTE.
   *
   * ORIGINALTEXT MARVIN (01.09.2026)
   *   „überall stehen die orte noch drin, die wir doch komplett rausfiltern.
   *    kannst du die bitte valide final überall in der plattform ausblenden."
   *
   * `ort.ignoriert` heisst „HIER ARBEITEN WIR NICHT" - so hiess es früher
   * (`IGNORIERTE_ORTE`), und daraus folgt beides: es gibt keine zuständige
   * Gemeinde, UND aus diesem Ort kommt kein Auftrag herein. Er ist
   * entschieden, erledigt und für die tägliche Arbeit uninteressant, stand
   * aber zwischen den gepflegten Zuordnungen und machte die Liste, in der man
   * etwas SUCHT, um seine Zahl länger.
   *
   * ER FÄLLT VOM BILDSCHIRM, NICHT AUS DER WELT. Bis zum 08.09.2026 stand er
   * in einer eigenen Karte darunter; seither steht er in den EINSTELLUNGEN
   * (Konfiguration → Betrieb → „Orte, in denen wir nicht arbeiten"). Ein
   * `filter` allein wäre die halbe Antwort: dann liesse sich ein einmal
   * gesperrter Ort nie mehr ändern - deshalb muss die Liste irgendwo stehen,
   * und sie steht ab jetzt dort, wo viermal danach gesucht wurde.
   */
  const gepflegt = (d.zeilen ?? []).filter((o: any) => !o.ignoriert);

  return (
    <>
      <Fehlerkasten lage={fehler} />

      {ergebnis && (
        <div className="hinweis">
          <b>{ergebnis.meldung}</b>
        </div>
      )}

      <Karte
        titel="Was ein Lauf ändern würde"
        rechts={darfPflegen ? (
          <span className="reihe">
            <button className="knopf klein"
                    disabled={laeuft || !z.fuellbar}
                    onClick={() => anwenden(false)}>
              {z.fuellbar} leere Gemeinden füllen
            </button>
            {z.korrigierbar > 0 && (
              <button className="knopf klein" disabled={laeuft}
                      onClick={() => anwenden(true)}>
                und {z.korrigierbar} korrigieren
              </button>
            )}
          </span>
        ) : undefined}
      >
        <p className="klein leise" style={{ marginTop: 0 }}>
          2 Ebenen: die gepflegte Zuordnung unten gilt immer und darf als
          einzige einen bereits eingetragenen Wert korrigieren. Sonst versucht
          es das System über den Namen und fängt dabei Zusätze wie „a. d. Lahn"
          und „(Westerwald)" ab. Ohne Treffer bleibt die Gemeinde LEER: das
          fällt auf. Eine geratene Gemeinde fällt nicht auf.
        </p>
        <div className="kachelreihe">
          <div className="kachel">
            <div className="wert">{z.auftraege}</div>
            <div className="was">Aufträge geprüft</div>
          </div>
          <div className="kachel">
            <div className="wert">{z.fuellbar}</div>
            <div className="was">leere Gemeinde, Treffer da</div>
          </div>
          <div className="kachel">
            <div className="wert"
                 style={z.korrigierbar
                   ? { color: 'var(--neu)' } : undefined}>
              {z.korrigierbar}
            </div>
            <div className="was">anders, gepflegte Zuordnung</div>
          </div>
          <div className="kachel">
            <div className="wert">{z.abweichend}</div>
            <div className="was">anders, nur Namensgleichheit</div>
          </div>
          <div className="kachel">
            <div className="wert"
                 style={z.ungeloest
                   ? { color: 'var(--ueberfaellig)' } : undefined}>
              {z.ungeloest}
            </div>
            <div className="was">Orte ohne Zuordnung</div>
          </div>
          {/* EINE ZAHL, DIE EINE AUFGABE IST. Sie steht nur da, wenn es sie
              gibt: eine Kachel, die immer „0" zeigt, wird nicht mehr
              gelesen. */}
          {z.ignoriert > 0 && (
            <div className="kachel">
              <div className="wert" style={{ color: 'var(--neu)' }}>
                {z.ignoriert}
              </div>
              <div className="was">in Orten ohne Arbeit</div>
            </div>
          )}
        </div>
        {/* BIS ZUM 08.09.2026 STAND HIER: „aber nur die, an denen KEINE Arbeit
            hängt". Der Satz war richtig und hat die Sperre wirkungslos
            gemacht - an fast jedem Auftrag hängt ein Termin, und damit blieb
            fast jeder stehen. */}
        {z.ignoriert > 0 && (
          <p className="klein" style={{ color: 'var(--neu)' }}>
            {z.ignoriert} Aufträge stehen in Orten, in denen wir nicht
            arbeiten. Der Lauf archiviert sie - <b>auch die, an denen Termine,
            Aufmaßzeilen, Dateien oder Papiere hängen</b>. Was daran hing, wird
            danach mit Namen genannt, und wer den Ort wieder freigibt, bekommt
            genau diese Aufträge zurück. Gesperrt wird unter Konfiguration →
            Betrieb.
          </p>
        )}
        {z.abweichend > 0 && (
          <p className="klein leise">
            Bei {z.abweichend} Aufträgen fände der Namensabgleich etwas
            anderes als das, was eingetragen ist. Er überschreibt NIE: der
            eingetragene Wert stammt von einem Menschen oder einem früheren
            Lauf, und ein zufällig gleicher Name ist kein Grund, ihn zu
            ersetzen.
          </p>
        )}
      </Karte>

      {!!offen?.length && (
        <Karte titel={`Orte ohne Zuordnung (${offen.length})`}>
          <table className="liste">
            <thead>
              <tr>
                <th>Ort</th>
                <th style={{ width: 120 }} className="zahl">Aufträge</th>
                <th style={{ width: 240 }}>Vorschlag</th>
                <th style={{ width: 160 }}></th>
              </tr>
            </thead>
            <tbody>
              {offen.map((o) => (
                <tr key={o.ort} style={{ cursor: 'default' }}>
                  <td>{o.ort}</td>
                  <td className="zahl">{o.auftraege}</td>
                  <td className="klein">
                    {o.vorschlag ?? (
                      <span className="leise">
                        keiner, es wird nichts geraten
                      </span>
                    )}
                  </td>
                  <td>
                    {darfPflegen && (
                      <button className="knopf klein" onClick={() => setNeu({
                        name: o.ort,
                        gemeinde_id: o.vorschlag_id ?? '',
                      })}>
                        Zuordnen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Karte>
      )}

      <Karte
        titel={`Gepflegte Zuordnungen (${gepflegt.length})`}
        rechts={darfPflegen ? (
          <button className="knopf klein" onClick={() => setNeu({
            name: '', gemeinde_id: '' })}>
            Ort zuordnen
          </button>
        ) : undefined}
      >
        {!gepflegt.length ? (
          <Leer text="Noch keine Zuordnung gepflegt. Solange greift nur der
                      Namensabgleich." />
        ) : (
          <table className="liste">
            <thead>
              <tr>
                <th>Ort</th><th style={{ width: 280 }}>Gemeinde</th>
                <th style={{ width: 120 }} className="zahl">Adressen</th>
                <th style={{ width: 150 }}></th>
              </tr>
            </thead>
            <tbody>
              {gepflegt.map((o: any) => (
                <tr key={o.name} style={{ cursor: 'default' }}>
                  <td>{o.name}</td>
                  <td>{o.gemeinde ?? <span className="rot">fehlt</span>}</td>
                  <td className="zahl">{o.adressen}</td>
                  <td>
                    {darfPflegen && (
                      <button className="knopf klein" onClick={() => setNeu({
                        name: o.name,
                        gemeinde_id: o.gemeinde_id ?? '',
                      })}>
                        Ändern
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Karte>

      {/* DIE KARTE „ORTE, IN DENEN WIR NICHT ARBEITEN" STAND HIER UND STEHT
          SEIT DEM 08.09.2026 IN DEN EINSTELLUNGEN.

          ORIGINALTEXT MARVIN
            „wo ist denn jetzt die liste um die orte, die uns nicht betreffen
             zu sperren ... derpunkt soll in die einstellungen"

          Sie hing an `gefiltert.length > 0` und war damit genau dann
          unsichtbar, wenn man sie sucht: **um die Liste zu sehen, musste
          schon ein Ort darin stehen.** Sie steht jetzt unter Konfiguration →
          Betrieb, immer, auch leer (`teile/Ortssperre.tsx`).

          HIER BLEIBT DIE GEMEINDEZUORDNUNG. Das sind zwei verschiedene
          Fragen - „welche Behörde bekommt die Post" und „arbeiten wir dort
          überhaupt" -, und sie standen bis zum 08.09.2026 in einer Karte.
          Zwei Wege zu derselben Sache sind kein doppelter Komfort, sondern
          eine halbe Auskunft: was umgezogen ist, ist hier weg. */}

      {neu && (
        <Dialog titel="Ort einer Gemeinde zuordnen" breite={560} laeuft={laeuft}
                zu={() => setNeu(null)}>
          <Fehlerkasten lage={fehler} />
          <Text name="name" titel="Ort" pflicht breite="100%"
                wert={neu.name} fehler={fehler}
                setze={(w) => setNeu({ ...neu, name: w })} />
          <Auswahl name="gemeinde_id" titel="Zuständige Gemeinde" breite="100%"
                   wert={neu.gemeinde_id} fehler={fehler}
                   setze={(w) => setNeu({ ...neu, gemeinde_id: w })}
                   optionen={d.gemeinden.map((g: any) =>
                     ({ wert: g.id, text: g.name }))} />
          {/* DER HAKEN „IGNORIEREN" STAND HIER UND IST WEG.
              -------------------------------------------------------------
              Er hiess „Für diesen Ort ist ausdrücklich keine Gemeinde
              zuständig" - nach seiner Bedeutung von vor dem 01.09.2026. Seit
              damals heisst er „hier arbeiten wir nicht", filtert den Import
              und archiviert den Bestand; die Aufschrift ist mitgewachsen und
              der Text nicht. **Ein Haken, der etwas anderes tut, als seine
              Aufschrift sagt, ist gefährlicher als einer, der fehlt.**

              Er steht jetzt als eigener Weg unter Konfiguration → Betrieb →
              „Orte, in denen wir nicht arbeiten", mit einer Probe davor: dort
              steht vor dem Klick, wie viele Aufträge er trifft.

              DIESES FENSTER ORDNET NUR NOCH ZU. Deshalb ist die Gemeinde hier
              wieder Pflicht - „ein leerer Eintrag ohne beides sagt nichts",
              und das ZWEITE von beidem gibt es hier nicht mehr. */}
          <div className="reihe" style={{ marginTop: 12 }}>
            <button className="knopf haupt"
                    disabled={laeuft || !neu.name.trim() || !neu.gemeinde_id}
                    onClick={speichern}>
              {laeuft ? 'Einen Moment' : 'Speichern'}
            </button>
            <button className="knopf" disabled={laeuft}
                    onClick={() => setNeu(null)}>Abbrechen</button>
          </div>
        </Dialog>
      )}
    </>
  );
}


// ============================================================================
// Regelpläne
// ============================================================================

/**
 * Die Regelplanliste.
 *
 * Bis heute liess sich kein einziger anlegen: die Tabelle war Lesestoff für
 * einen Dialog, den niemand befüllen konnte.
 *
 * GELÖSCHT WIRD NICHT, sondern ausser Kraft gesetzt. Ein Regelplan, der einmal
 * in einem Antrag an eine Behörde stand, muss erklärbar bleiben.
 */
export function Regelplaene({ ich }: { ich: Anmelder }) {
  const [zeilen, setZeilen] = useState<any[] | null>(null);
  const [neu, setNeu] = useState<any | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const darfPflegen = darf(ich, 'regelplan.pflegen');
  const [pdfLaeuft, setPdfLaeuft] = useState(false);
  const [pdfBefund, setPdfBefund] = useState<string | null>(null);

  async function laden() { setZeilen(await api.regelplaene()); }
  useEffect(() => { laden().catch((f) => setFehler(ausFehler(f))); }, []);
  useFrisch(laden);
  if (!zeilen) return null;

  async function speichern() {
    setLaeuft(true); setFehler(null);
    try { await api.regelplanSetzen(neu); setNeu(null); await laden(); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  /* DIE PDF DAZU (.242). Ohne sie hat die Freigabe nichts anzuhängen. */
  async function pdfHoch(dateien: File[], id?: string) {
    setPdfLaeuft(true); setFehler(null); setPdfBefund(null);
    try {
      const r = await api.regelplanDateien(dateien, id);
      setPdfBefund(
        (r.zugeordnet.length ? `Hinterlegt: ${r.zugeordnet.map((z) => `${z.regelplan} (${z.datei})`).join(', ')}.` : '')
        + (r.ohne.length ? ` Keinem Regelplan zuzuordnen: ${r.ohne.join(', ')}. Diese bitte einzeln über „PDF" in der Zeile hinterlegen.` : ''));
      await laden();
    } catch (f) { setFehler(ausFehler(f)); } finally { setPdfLaeuft(false); }
  }
  function pdfWaehlen(id: string) {
    const i = document.createElement('input');
    i.type = 'file'; i.accept = '.pdf,application/pdf';
    i.onchange = () => { if (i.files?.length) pdfHoch([...i.files], id); };
    i.click();
  }
  const ohnePdf = zeilen.filter((r) => r.aktiv && !r.datei_id).length;

  return (
    <>
      <Fehlerkasten lage={fehler} />
      <Karte
        titel={`Regelpläne (${zeilen.filter((r) => r.aktiv).length} aktiv)`}
        rechts={darfPflegen ? (
          <button className="knopf klein" onClick={() => setNeu({
            datei: '', anzeige: '', verkehrsbereich: '', massnahme_art: '',
            ampel: '', beschreibung: '', aktiv: true })}>
            Regelplan anlegen
          </button>
        ) : undefined}
      >
        <p className="klein leise" style={{ marginTop: 0 }}>
          Beim Vorbereiten wird nicht gefiltert, sondern sortiert: die
          passenden stehen oben, alle übrigen darunter. Ein widersprüchlicher
          wird gemeldet und nicht abgewiesen. Außer Kraft gesetzte werden gar
          nicht mehr vorgeschlagen, bleiben aber stehen: was einmal in einem
          Antrag stand, muss erklärbar bleiben.
        </p>
        {darfPflegen && (
          <Abwurf klein accept=".pdf,application/pdf" laeuft={pdfLaeuft}
            laufText="Regelpläne werden hinterlegt …"
            titel={ohnePdf ? `PDF der Regelpläne hier ablegen (${ohnePdf} ohne PDF)` : 'PDF der Regelpläne hier ablegen'}
            hinweis="Mehrere auf einmal: jede Datei wird über ihren Namen dem Regelplan zugeordnet. Diese PDF geht bei AAZ/VAO als Anlage mit."
            nimm={(st) => pdfHoch(st.map((x) => x.datei))} />
        )}
        {pdfBefund && <p className="klein" style={{ margin: '8px 0' }}>{pdfBefund}</p>}
        <table className="liste">
          <thead>
            <tr>
              <th style={{ width: 190 }}>Datei</th>
              <th>Bezeichnung</th>
              <th style={{ width: 130 }}>Bereich</th>
              <th style={{ width: 90 }}>Ampel</th>
              <th style={{ width: 110 }} className="zahl">verwendet</th>
              <th style={{ width: 170 }}></th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((r) => (
              <tr key={r.id} style={{ cursor: 'default',
                                      opacity: r.aktiv ? 1 : .5 }}>
                <td className="klein">
                  {r.datei}
                  <div>
                    {r.datei_id
                      ? <button className="knopf klein" style={{ marginTop: 4 }}
                          onClick={() => extern(api.regelplanDateiAdresse(r.id))}>PDF ansehen</button>
                      : <span className="merker warnung" style={{ marginTop: 4, display: 'inline-block' }}>ohne PDF</span>}
                  </div>
                </td>
                <td>
                  {r.anzeige}
                  {r.beschreibung && (
                    <div className="klein leise">{r.beschreibung}</div>
                  )}
                  {!r.aktiv && (
                    <span className="merker" style={{ marginLeft: 6 }}>
                      außer Kraft
                    </span>
                  )}
                </td>
                <td className="klein">{r.verkehrsbereich ?? ''}</td>
                <td className="klein">
                  {r.ampel === null ? <span className="leise">offen</span>
                                    : r.ampel ? 'ja' : 'nein'}
                </td>
                <td className="zahl">{r.verwendet}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {darfPflegen && (
                    <button className="knopf klein" style={{ marginRight: 4 }}
                            disabled={pdfLaeuft} onClick={() => pdfWaehlen(r.id)}>
                      PDF
                    </button>
                  )}
                  {darfPflegen && (
                    <button className="knopf klein" onClick={() => setNeu({
                      ...r,
                      ampel: r.ampel === null ? '' : (r.ampel ? 'ja' : 'nein'),
                      verkehrsbereich: r.verkehrsbereich ?? '',
                      massnahme_art: r.massnahme_art ?? '',
                      beschreibung: r.beschreibung ?? '',
                    })}>
                      Ändern
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!zeilen.length && (
              <tr>
                <td colSpan={6} style={{ padding: 20 }}>
                  <Leer text="Kein Regelplan hinterlegt. Solange lässt sich beim
                              Vorbereiten keiner auswählen." />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Karte>

      {neu && (
        <Dialog titel="Regelplan" breite={640} laeuft={laeuft}
                zu={() => setNeu(null)}>
          <Fehlerkasten lage={fehler} />
          <div className="feldreihe">
            <Text name="datei" titel="Dateiname" pflicht breite={260}
                  wert={neu.datei} fehler={fehler}
                  setze={(w) => setNeu({ ...neu, datei: w })}
                  hinweis="Der Schlüssel. Unter diesem Namen steht der Plan
                           später in der Anordnung." />
            <Text name="anzeige" titel="Bezeichnung" pflicht breite={300}
                  wert={neu.anzeige} fehler={fehler}
                  setze={(w) => setNeu({ ...neu, anzeige: w })} />
          </div>
          <div className="feldreihe">
            <Text name="verkehrsbereich" titel="Verkehrsbereich" breite={200}
                  wert={neu.verkehrsbereich} fehler={fehler}
                  setze={(w) => setNeu({ ...neu, verkehrsbereich: w })}
                  platzhalter="Gehweg, Fahrbahn, Parkstreifen" />
            <Text name="massnahme_art" titel="Art der Maßnahme" breite={220}
                  wert={neu.massnahme_art} fehler={fehler}
                  setze={(w) => setNeu({ ...neu, massnahme_art: w })} />
            <Auswahl name="ampel" titel="Ampel" breite={140}
                     wert={neu.ampel} fehler={fehler}
                     leerText="nicht festgelegt"
                     setze={(w) => setNeu({ ...neu, ampel: w })}
                     optionen={[{ wert: 'ja', text: 'ja' },
                                { wert: 'nein', text: 'nein' }]} />
          </div>
          <Text name="beschreibung" titel="Beschreibung" mehrzeilig wert={neu.beschreibung} fehler={fehler}
                setze={(w) => setNeu({ ...neu, beschreibung: w })} />
          <Haken name="aktiv" titel="Wird beim Vorbereiten vorgeschlagen"
                 wert={neu.aktiv !== false}
                 setze={(w) => setNeu({ ...neu, aktiv: w })}
                 hinweis="Der Haken weg heisst: ausser Kraft. Gelöscht wird
                          nicht." />
          <div className="reihe" style={{ marginTop: 12 }}>
            <button className="knopf haupt"
                    disabled={laeuft || !neu.datei.trim() || !neu.anzeige.trim()}
                    onClick={() => speichern()}>
              {laeuft ? 'Einen Moment' : 'Speichern'}
            </button>
            <button className="knopf" disabled={laeuft}
                    onClick={() => setNeu(null)}>Abbrechen</button>
          </div>
        </Dialog>
      )}
    </>
  );
}


// ============================================================================
// Das Handbuch stand hier, und es steht jetzt auf einer eigenen Seite
// ============================================================================
//
// ORIGINALTEXT MARVIN (27.08.2026)
//   „das handbuch kannst du raus als richtigen menüpunkt machen"
//
// Es war ein Reiter in der Konfiguration, also hinter einem Recht, das kein
// Monteur und keine Partnerbauleitung hat. Seit Migration 052 ist es ein
// Menüpunkt mit eigenem Leserecht: `web/src/seiten/Handbuch.tsx`.
